import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { finalize } from 'rxjs';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { DynamicApiService, DynamicEntity, DynamicMigration } from './dynamic-api.service';

type SelectOption = { label: string; value: string | null };

interface StatusOption {
  labelKey: string;
  value: string;
}

@Component({
  standalone: true,
  selector: 'ain-dynamic-migrations',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    TableModule,
    SelectModule,
    TagModule,
    FormsModule,
    RouterLink,
    AinDatePipe
  ],
  template: `
    <ain-page [title]="t('dynamic.migrations.title')" [description]="t('dynamic.migrations.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'dynamic.migration.info.title'"
        [descriptionKey]="'dynamic.migration.info.description'"
        [bulletKeys]="[
          'dynamic.migration.info.bullets.review',
          'dynamic.migration.info.bullets.backup',
          'dynamic.migration.info.bullets.rollback'
        ]" />

      <div class="filters card">
        <label>
          <span>{{ t('dynamic.migrations.filter.entity') }}</span>
          <p-select
            [options]="entityOptions()"
            optionLabel="label"
            optionValue="value"
            [(ngModel)]="entityFilter"
            name="entityFilter"
            [showClear]="true"
            [filter]="true"
            appendTo="body"
            (ngModelChange)="loadMigrations()" />
        </label>
        <label>
          <span>{{ t('dynamic.migrations.filter.status') }}</span>
          <p-select
            [options]="statusOptions"
            optionValue="value"
            [(ngModel)]="statusFilter"
            name="statusFilter"
            [showClear]="true"
            appendTo="body">
            <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : t('common.all') }}</ng-template>
            <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
          </p-select>
        </label>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.loadFailed')" [message]="error() ?? ''" />
      } @else if (!filteredMigrations().length) {
        <ain-state type="empty" [title]="t('common.noRecords')" />
      } @else {
        <p-table [value]="filteredMigrations()" dataKey="id" [paginator]="true" [rows]="10">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('dynamic.migrations.columns.entity') }}</th>
              <th>{{ t('dynamic.migration.changeDescription') }}</th>
              <th>{{ t('dynamic.migration.status') }}</th>
              <th>{{ t('dynamic.migration.isDestructive') }}</th>
              <th>{{ t('dynamic.migrations.columns.createdAt') }}</th>
              <th style="width: 6rem"></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ entityLabel(row.dynamicEntityId) }}</td>
              <td>{{ row.changeDescription || '—' }}</td>
              <td>
                <p-tag [value]="statusLabel(row.status)" [severity]="statusSeverity(row.status)" />
              </td>
              <td>
                @if (row.isDestructive) {
                  <p-tag [value]="t('common.yes')" severity="danger" />
                } @else {
                  {{ t('common.no') }}
                }
              </td>
              <td>
                @if (row.requestedAtUtc) {
                  {{ row.requestedAtUtc | ainDate:'datetime' }}
                } @else {
                  —
                }
              </td>
              <td>
                <a class="row-link" [routerLink]="['/dynamic/migrations', row.id]">{{ t('common.open') }}</a>
              </td>
            </tr>
          </ng-template>
        </p-table>
      }
    </ain-page>
  `,
  styles: [`
    .filters { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 12px; border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; }
    .filters label { display: grid; gap: 6px; min-width: 220px; flex: 1; }
    .filters span { font-size: 12px; color: #475569; font-weight: 600; }
    .row-link { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
  `]
})
export class DynamicMigrationsComponent implements OnInit {
  private readonly api = inject(DynamicApiService);
  private readonly localization = inject(LocalizationService);
  private readonly destroyRef = inject(DestroyRef);

  readonly migrations = signal<DynamicMigration[]>([]);
  readonly entities = signal<DynamicEntity[]>([]);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);

  entityFilter: string | null = null;
  statusFilter: string | null = null;

  readonly statusOptions: StatusOption[] = [
    { labelKey: 'dynamic.migration.status.pending', value: 'PendingApproval' },
    { labelKey: 'dynamic.migration.status.approved', value: 'Approved' },
    { labelKey: 'dynamic.migration.status.executing', value: 'Executing' },
    { labelKey: 'dynamic.migration.status.executed', value: 'Succeeded' },
    { labelKey: 'dynamic.migration.status.failed', value: 'Failed' },
    { labelKey: 'dynamic.migration.status.rolledBack', value: 'RolledBack' },
    { labelKey: 'dynamic.migration.status.rollbackRequested', value: 'RollbackRequested' }
  ];

  readonly entityOptions = computed<SelectOption[]>(() =>
    this.entities().map(entity => ({
      label: entity.displayName || entity.name,
      value: entity.id
    }))
  );

  filteredMigrations(): DynamicMigration[] {
    const status = this.statusFilter;
    if (!status) {
      return this.migrations();
    }

    return this.migrations().filter(item => item.status === status);
  }

  ngOnInit(): void {
    this.api.listEntities().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: items => this.entities.set(items),
      error: () => this.entities.set([])
    });

    this.loadMigrations();
  }

  loadMigrations(): void {
    this.loading.set(true);
    this.error.set(null);

    this.api.listMigrations(this.entityFilter ?? undefined).pipe(
      finalize(() => this.loading.set(false))
    ).subscribe({
      next: items => this.migrations.set(items),
      error: err => {
        this.migrations.set([]);
        this.error.set(err?.message ?? this.t('common.loadFailed'));
      }
    });
  }

  entityLabel(entityId: string): string {
    const entity = this.entities().find(item => item.id === entityId);
    return entity?.displayName ?? entity?.name ?? entityId;
  }

  statusLabel(status: string): string {
    const key = `dynamic.migration.status.${this.normalizeStatusKey(status)}`;
    const translated = this.t(key);
    return translated === key ? status : translated;
  }

  statusSeverity(status: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
    switch (status) {
      case 'PendingApproval':
        return 'warn';
      case 'Approved':
      case 'Executing':
        return 'info';
      case 'Succeeded':
        return 'success';
      case 'Failed':
        return 'danger';
      case 'RolledBack':
        return 'secondary';
      case 'RollbackRequested':
        return 'warn';
      default:
        return 'secondary';
    }
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private normalizeStatusKey(status: string): string {
    switch (status) {
      case 'PendingApproval':
        return 'pending';
      case 'Approved':
        return 'approved';
      case 'Executing':
        return 'executing';
      case 'Succeeded':
        return 'executed';
      case 'Failed':
        return 'failed';
      case 'RolledBack':
        return 'rolledBack';
      case 'RollbackRequested':
        return 'rollbackRequested';
      default:
        return status.charAt(0).toLowerCase() + status.slice(1);
    }
  }
}
